import {
  BatchClientAnalyticsEvent,
  BatchClientAnalyticsTuples,
  CONTENT_TYPE_HEADER,
  CONTENT_TYPE_VALUE_JSON,
  ClientAnalyticsEventType,
  type ClientAnalyticsPageData,
  DIRECT_REFERRER,
  isUuid,
  sanitizeClientAnalyticsPageData,
  sanitizeUrlForAnalytics,
  uuid,
} from "@hex/common";

import {
  anonymousUserIdKey,
  getDecryptedCookieBrowser,
  getEncryptedValueBrowser,
} from "@rudderstack/analytics-js-cookies";

import { ORG_ID } from "../orgs";

import { safeSessionStorage } from "./browserStorage.js";
import { ClientCounterEvent } from "./clientCounterEvent";
import { createRequestHeaders } from "./headers";
import { getOriginOverrideUrlObject } from "./mcpOverrides";

export const TRACK_EVENT_TIMEOUT_MS = 1000;

let queuedEvents: BatchClientAnalyticsEvent[] = [];
let currentTimeout: number | undefined;

export const trackEvent = <K extends BatchClientAnalyticsTuples>(
  ...[event, properties]: K
): void => {
  queuedEvents.push({ event, properties } as BatchClientAnalyticsEvent);

  if (currentTimeout == null) {
    currentTimeout = window.setTimeout(
      () => sendEvents(),
      TRACK_EVENT_TIMEOUT_MS,
    );
  }
};

export const trackCounterEvent = (eventType: ClientCounterEvent): void => {
  trackEvent(ClientAnalyticsEventType.CLIENT_COUNTER_EVENT, {
    eventType,
  });
};

/**
 * This is invoked from the highest level component to ensure that we will send
 * all events before session closes.
 */
export const sendEvents = (): void => {
  window.clearTimeout(currentTimeout);
  currentTimeout = undefined;

  if (queuedEvents.length === 0) {
    return;
  }
  const urlOverride = getOriginOverrideUrlObject();
  const base = urlOverride?.origin ?? "";

  void fetch(`${base}/track-events`, {
    // setting keepalive to true to ensure that the request is sent even if
    // session ends.
    keepalive: true,
    method: "POST",
    headers: {
      [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE_JSON,
      ...createRequestHeaders(),
    },
    body: JSON.stringify({
      events: queuedEvents,
      orgId: ORG_ID,
    }),
  });
  queuedEvents = [];
};

// Stores the referrer of the first page in the session. The Rudderstack JS SDK
// used to track this internally; with the SDK removed we keep it ourselves. Held
// in sessionStorage so it resets per session, matching the SDK's behavior.
const INITIAL_REFERRER_KEY = "hex_initial_referrer";

const HEX_VISITOR_ID_COOKIE = "hex_visitor_id";

// One year, matching the persistence the Rudderstack SDK used for the anon id.
const ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

// In-memory cache so the id stays stable within a page load even if the browser
// blocks cookies (we then fall back to sending it in the request body).
let cachedAnonymousId: string | undefined;

const getHexTechCookieDomain = (): string | undefined => {
  const { hostname } = window.location;
  if (hostname === "hex.tech" || hostname.endsWith(".hex.tech")) {
    return ".hex.tech";
  }
  return undefined;
};

const getHexVisitorIdFromCookie = (): string | undefined => {
  const prefix = `${HEX_VISITOR_ID_COOKIE}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (!trimmed.startsWith(prefix)) {
      continue;
    }
    let value: string;
    try {
      value = decodeURIComponent(trimmed.slice(prefix.length));
    } catch {
      return undefined;
    }
    return isUuid(value) ? value : undefined;
  }
  return undefined;
};

const persistHexVisitorIdCookie = (visitorId: string): void => {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const domain = getHexTechCookieDomain();
  const domainAttribute = domain != null ? `; domain=${domain}` : "";
  document.cookie = `${HEX_VISITOR_ID_COOKIE}=${encodeURIComponent(
    visitorId,
  )}; path=/; max-age=${ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}${domainAttribute}`;
};

const persistAnonymousIdCookie = (anonymousId: string): void => {
  const encrypted = getEncryptedValueBrowser(anonymousId);
  if (encrypted == null) {
    return;
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  // Host-scoped (no explicit domain) so it works across local dev, hex.tech, and
  // single-tenant domains. encodeURIComponent matches how the cookies package
  // reads the value back (it decodeURIComponent's on read), and cookie-parser
  // decodes it the same way server-side.
  document.cookie = `${anonymousUserIdKey}=${encodeURIComponent(
    encrypted,
  )}; path=/; max-age=${ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
};

/**
 * Returns the Rudderstack anonymous id. Prefers the cross-subdomain
 * `hex_visitor_id` cookie (set on hex.tech subdomains by marketing, or by this
 * app when minting a new id). Falls back to the legacy host-scoped
 * `rl_anonymous_id` cookie on other domains.
 */
const getOrCreateAnonymousId = (): string => {
  if (cachedAnonymousId != null) {
    return cachedAnonymousId;
  }
  const hexVisitorId = getHexVisitorIdFromCookie();
  if (hexVisitorId != null) {
    cachedAnonymousId = hexVisitorId;
    return hexVisitorId;
  }
  const existing = getDecryptedCookieBrowser(anonymousUserIdKey);
  if (typeof existing === "string" && isUuid(existing)) {
    persistHexVisitorIdCookie(existing);
    cachedAnonymousId = existing;
    return existing;
  }
  const anonymousId = uuid();
  persistHexVisitorIdCookie(anonymousId);
  persistAnonymousIdCookie(anonymousId);
  cachedAnonymousId = anonymousId;
  return anonymousId;
};

const getInitialReferrer = (): string => {
  // safeSessionStorage returns null when storage is unavailable (private mode /
  // disabled), in which case we fall back to the current referrer.
  const existing = safeSessionStorage.getItem(INITIAL_REFERRER_KEY);
  if (existing != null) {
    return existing;
  }
  const referrer =
    document.referrer === ""
      ? DIRECT_REFERRER
      : sanitizeUrlForAnalytics(document.referrer);
  safeSessionStorage.setItem(INITIAL_REFERRER_KEY, referrer);
  return referrer;
};

const collectPageData = (): ClientAnalyticsPageData => {
  const initialReferrer = getInitialReferrer();
  return sanitizeClientAnalyticsPageData({
    path: window.location.pathname,
    url: window.location.href,
    tab_url: window.location.href,
    search: window.location.search,
    title: document.title,
    referrer: document.referrer,
    initial_referrer: initialReferrer,
  });
};

/**
 * Records a page view by routing it to the server, which forwards it to
 * Rudderstack. Replaces the former direct `RUDDER_ANALYTICS.page()` call so the
 * Rudderstack client SDK (and its public write key) no longer ship to the
 * browser. Browser-only page context is captured here and sent to the server.
 */
export const trackPage = (): void => {
  const base = getOriginOverrideUrlObject()?.origin ?? "";

  void fetch(`${base}/track-page`, {
    // keepalive ensures the request is sent even as the page is unloading.
    keepalive: true,
    method: "POST",
    headers: {
      [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE_JSON,
      ...createRequestHeaders(),
    },
    body: JSON.stringify({
      orgId: ORG_ID,
      page: collectPageData(),
      anonymousId: getOrCreateAnonymousId(),
    }),
  });
};

/**
 * Identifies the current user by routing to the server, which derives all
 * identify traits server-side from the authenticated user. Replaces the former
 * direct `RUDDER_ANALYTICS.identify()` call; no traits are sent from the client.
 */
export const identifyUser = (): void => {
  const base = getOriginOverrideUrlObject()?.origin ?? "";

  void fetch(`${base}/track-identify`, {
    keepalive: true,
    method: "POST",
    headers: {
      [CONTENT_TYPE_HEADER]: CONTENT_TYPE_VALUE_JSON,
      ...createRequestHeaders(),
    },
    body: JSON.stringify({
      orgId: ORG_ID,
      anonymousId: getOrCreateAnonymousId(),
    }),
  });
};

/** @internal Exported for unit tests. */
export const trackEventInternals = {
  INITIAL_REFERRER_KEY,
  collectPageData,
};
